// frontend/src/services/parent.service.ts

import { authService } from './auth.service';
import {
  ChildSummary,
  ChildOverview,
  ChildAIInsight,
  WeeklyReport,
  AiTopicsReport,
  ActivityReport,
  ReportPeriod,
  CourseLessonDetails,
} from '../types/parent.types';

const API_BASE = (import.meta.env.PROD && (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL)) ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

class ParentApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ParentApiError';
  }
}

async function authedFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await authService.getAccessToken();
  if (!token) throw new ParentApiError('Oturum bulunamadı, lütfen giriş yapın.');

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ParentApiError(data?.error?.message || `İstek başarısız (HTTP ${res.status})`);
  }
  return data as T;
}

export const parentService = {
  async listChildren(): Promise<ChildSummary[]> {
    const data = await authedFetch<{ children: ChildSummary[] }>('/api/parent/children');
    return data.children;
  },

  async getChildOverview(childId: string): Promise<ChildOverview> {
    return authedFetch<ChildOverview>(`/api/parent/children/${childId}/overview`);
  },

  async getChildAIInsight(childId: string): Promise<ChildAIInsight> {
    return authedFetch<ChildAIInsight>(`/api/parent/children/${childId}/ai-insight`);
  },

  async getWeeklyReport(childId: string): Promise<WeeklyReport> {
    return authedFetch<WeeklyReport>(`/api/parent/children/${childId}/weekly-report`);
  },

  async getAiTopics(childId: string): Promise<AiTopicsReport> {
    return authedFetch<AiTopicsReport>(`/api/parent/children/${childId}/ai-topics`);
  },

  async getActivityReport(childId: string, period: ReportPeriod): Promise<ActivityReport> {
    return authedFetch<ActivityReport>(`/api/parent/children/${childId}/activity?period=${period}`);
  },

  async getCourseLessons(childId: string, courseId: string): Promise<CourseLessonDetails> {
    return authedFetch<CourseLessonDetails>(`/api/parent/children/${childId}/courses/${courseId}/lessons`);
  },
};

export { ParentApiError };
